import { Box, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  F0AvatarList,
  F0Icon,
  F0TagAlert,
  IconType,
  PersonAvatarVariant,
} from '@factorialco/f0-react'
import {
  CalendarArrowLeft as CalendarArrowLeftIcon,
  CalendarArrowRight as CalendarArrowRightIcon,
  Calendar as CalendarIcon,
  Comment as CommentIcon,
  Paperclip as PaperclipIcon,
  People as PeopleIcon,
  Suitcase as SuitcaseIcon,
} from '@factorialco/f0-react/icons/app'
import { ProjectTaskForKanbanFragment } from 'generated/resources/graphql'
import moment from 'moment'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

interface KanbanCardItemProps {
  icon?: IconType | undefined
  iconTooltipContent?: string
  content: string | React.ReactNode
}

const KanbanCardItem = ({ icon, content, iconTooltipContent }: KanbanCardItemProps) => {
  const [open, toggle] = useToggle()

  return (
    <Box flexDirection="row" alignItems="center">
      {icon && (
        <Tooltip.Alt
          open={open && !!iconTooltipContent}
          toggle={toggle}
          attachment="bottom center"
          label={iconTooltipContent}
        >
          <Box paddingRight="s6">
            <F0Icon icon={icon} size="sm" className="text-f1-icon" aria-hidden />
          </Box>
        </Tooltip.Alt>
      )}

      {typeof content === 'string' ? (
        <Text size="200" weight="normal" color="grey1000">
          {content}
        </Text>
      ) : (
        content
      )}
    </Box>
  )
}

const getDateStatus = (dueDate: string | null | undefined) => {
  if (!dueDate) return { isOverdue: false, isToday: false }

  const dueMoment = moment(dueDate)
  const today = moment()

  return {
    isOverdue: dueMoment.isBefore(today, 'day'),
    isToday: dueMoment.isSame(today, 'day'),
  }
}

const datesProjection = (startDate?: string | null, dueDate?: string | null) => {
  if (!startDate && !dueDate) return null

  const bothDates = startDate && dueDate
  const isOnlyDueDate = !startDate && dueDate
  const { isOverdue, isToday } = getDateStatus(dueDate)

  const formattedStartDate = startDate ? dateBackendToUI(startDate) : '-'
  const formattedDueDate = dueDate ? dateBackendToUI(dueDate) : '-'

  return (
    <Box width="fit-content">
      {isOverdue ? (
        <KanbanCardItem
          icon={CalendarArrowLeftIcon}
          content={<F0TagAlert level="critical" text={`${formattedDueDate}`} />}
          iconTooltipContent={i18n.t('project_management.tasks.form.labels.due_date')}
        />
      ) : isToday ? (
        <KanbanCardItem
          icon={CalendarArrowLeftIcon}
          content={<F0TagAlert level="warning" text={`${formattedDueDate}`} />}
          iconTooltipContent={i18n.t('project_management.tasks.form.labels.due_date')}
        />
      ) : bothDates ? (
        <KanbanCardItem
          icon={CalendarIcon}
          content={`${formattedStartDate} - ${formattedDueDate}`}
          iconTooltipContent={i18n.t('project_management.tasks.form.labels.dates')}
        />
      ) : isOnlyDueDate ? (
        <KanbanCardItem
          icon={CalendarArrowLeftIcon}
          content={`${formattedDueDate}`}
          iconTooltipContent={i18n.t('project_management.tasks.form.labels.due_date')}
        />
      ) : (
        <KanbanCardItem
          icon={CalendarArrowRightIcon}
          content={`${formattedStartDate}`}
          iconTooltipContent={i18n.t('project_management.tasks.form.labels.start_date')}
        />
      )}
    </Box>
  )
}

const subprojectProjection = (subproject: ProjectTaskForKanbanFragment['subproject']) => {
  return (
    <>
      {subproject && (
        <KanbanCardItem
          icon={SuitcaseIcon}
          content={subproject?.name}
          iconTooltipContent={i18n.t('project_management.tasks.form.labels.subproject')}
        />
      )}
    </>
  )
}
const assigneesProjection = (task: ProjectTaskForKanbanFragment['task']) => {
  const assignees = task?.assigneesConnection.edges.map((edge) => edge.node)
  if (assignees?.length === 0) return null
  const avatars: PersonAvatarVariant[] = []
  assignees?.forEach((assignee) => {
    avatars.push({
      src: buildUrl(assignee.avatar) ?? undefined,
      firstName: assignee.firstName,
      lastName: assignee.lastName,
      type: 'person',
    })
  })

  return (
    <KanbanCardItem
      icon={PeopleIcon}
      content={<F0AvatarList avatars={avatars} size="xs" noTooltip type="person" />}
      iconTooltipContent={i18n.t('project_management.tasks.form.labels.assignees')}
    />
  )
}

const commentsProjection = (task: ProjectTaskForKanbanFragment['task']) => {
  const commentsCount = task?.commentsConnection.totalCount ?? 0

  return <>{commentsCount > 0 && <KanbanCardItem icon={CommentIcon} content={commentsCount} />}</>
}

const attachmentsProjection = (task: ProjectTaskForKanbanFragment['task']) => {
  const attachmentsCount = task?.attachmentsConnection.totalCount ?? 0

  return (
    <>
      {attachmentsCount > 0 && <KanbanCardItem icon={PaperclipIcon} content={attachmentsCount} />}
    </>
  )
}

const relativesProjection = (task: ProjectTaskForKanbanFragment['task']) => {
  return (
    <Box flexDirection="row" gap="s12">
      {commentsProjection(task)}
      {attachmentsProjection(task)}
    </Box>
  )
}

export const kanbanProjections = (projectTask: ProjectTaskForKanbanFragment) => {
  return [
    subprojectProjection(projectTask.subproject),
    datesProjection(projectTask.task?.startsOn, projectTask.task?.dueOn),
    assigneesProjection(projectTask.task),
    relativesProjection(projectTask.task),
  ]
}
